import { useCallback } from "react";

import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";

import { buildGroupFetchPayload } from "../utils/teacher/InputRatingUtils";
import { groupNameNormalizer } from "../utils/GroupNameNormalizer";
import { toast } from "react-toastify";

const useGroupData = ({
    gradingProfiles,
    schoolYearKey,
    selectedGroups,
    selectedEpr,
    selectedGrade,
    selectedTemplate,
    setAllSharedGroupOptions,
    setExistingStudentComments,
    setGroupOptions,
    setSelectedGroupContent,
    initRatings,
    sharedGradingProfileDtos,
    timePeriod,
    token,
    userDetails,
    overrideTeacherGuid
}) => {
    const fetchSelectedGroupInfo = useCallback(async () => {
        if (!selectedGroups?.length || !selectedTemplate) return;
        const defaultGradingProfile =
            !selectedEpr && selectedGrade ? gradingProfiles?.find((p) => p.gradeName === selectedGrade) : null;

        const effectiveEpr = selectedEpr ? selectedEpr : defaultGradingProfile;

        const effectiveTeacherGuid = overrideTeacherGuid || userDetails?.uid;
        const payload = buildGroupFetchPayload({
            selectedGroups,
            selectedEpr: effectiveEpr,
            selectedTemplate,
            teacherGuid: effectiveTeacherGuid
        });

        const options = {
            action: "getGroupsRatings",
            data: payload,
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const payload = response.data.payload;

                    // Build sharedGroupId → sharedGradingCriteria from selection metadata.
                    // sharedGradingCriteria is not on the server response — it lives on the
                    // UI selection option's sharedMeta and must be joined here.
                    const selectedGroupIdsToMeta = {};
                    selectedGroups.forEach((group) => {
                        if (group.groupType === "SHARED" && group.sharedMeta) {
                            selectedGroupIdsToMeta[group.sharedMeta.sharedGroupId] =
                                group.sharedMeta.sharedGradingCriteria ?? group.matchingCriteriaId;
                        }
                    });

                    const enrichedGroups = payload.map((group) => {
                        const sharedGradingCriteria = selectedGroupIdsToMeta[group.groupId] || null;
                        const sharedProfileDto = sharedGradingProfileDtos.find(
                            (sharedDto) => sharedDto.key == group.gradingProfileId
                        );
                        const gradingProfileStatus = group.shareGroupId
                            ? sharedProfileDto?.status
                            : effectiveEpr?.status;

                        return {
                            ...group,
                            sharedGradingCriteria,
                            gradingProfileStatus
                        };
                    });

                    setSelectedGroupContent(enrichedGroups);

                    // Initialize ratings immediately from the fresh server data.
                    if (initRatings) {
                        const flat = enrichedGroups.flatMap((group) =>
                            (group.groupStudentContents || []).map((s) => ({
                                key: `${group.groupId}-${s.gradingProfileStudentId}`,
                                contentRatings: s.contentRatings || []
                            }))
                        );
                        initRatings(flat);
                    }
                }
            })
            .catch((error) => {
                toast.error("There was a problem loading some group information. Please try again later.");
                console.error(error);
            });
    }, [
        gradingProfiles,
        initRatings,
        overrideTeacherGuid,
        selectedEpr,
        selectedGrade,
        selectedGroups,
        selectedTemplate,
        setSelectedGroupContent,
        sharedGradingProfileDtos,
        token,
        userDetails
    ]);

    const fetchSelectedGroupStudentComments = useCallback(async () => {
        if (!selectedGroups?.length || !selectedTemplate) return;
        const teacherGuid = userDetails?.uid;

        const payload = buildGroupFetchPayload({ selectedGroups, selectedEpr, selectedTemplate, teacherGuid });
        const options = {
            action: "getGroupStudentCriteriaComments",
            data: payload,
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const payload = response.data.payload;
                    setExistingStudentComments(payload);
                }
            })
            .catch((error) => {
                toast.error("There was a problem loading existing comments.");
                console.error(error);
            });
    }, [selectedGroups, selectedEpr, selectedTemplate, setExistingStudentComments, userDetails, token]);

    const fetchAllGroupOptions = useCallback(() => {
        if (!selectedTemplate || !token) return;

        const defaultGradingProfile = selectedGrade
            ? gradingProfiles?.find((p) => p.gradeName === selectedGrade)
            : null;
        if (!selectedEpr && !defaultGradingProfile) return;
        const effectiveEpr = selectedEpr || defaultGradingProfile;
        const gradingCriteria = effectiveEpr.gradingCriteriaList.find(
            (criteria) => parseInt(criteria.parentTemplate, 10) === selectedTemplate.templateId
        );

        if (!gradingCriteria) return;

        const options = {
            action: "getGroups",
            params: {
                fetchAllSharedGroups: true,
                gradingCriteriaId: gradingCriteria.key
            },
            gradingProfileId: effectiveEpr.key,
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const homeroomGroups = response.data.payload.teacherHomeroomGroups;
                    const sharedGroups = response.data.payload.sharedGroupInfos.filter(
                        (group) => Number(group.timePeriod) === Number(timePeriod)
                    );
                    const groupOptions = [
                        {
                            label: "Homeroom Groups",
                            options: homeroomGroups
                                .filter((g) => !g.groupName.includes("INTERNAL_"))
                                .map((group) => ({
                                    value: `homeroom-${group.groupId}`,
                                    label: group.groupName,
                                    groupType: "HOMEROOM"
                                }))
                        },
                        {
                            label: "Groups Shared With Me",
                            options: sharedGroups.map((group) => ({
                                value: `shared-${group.sharedGroupId}`,
                                label: `${group.teacherName} - ${groupNameNormalizer(group.sharedGroupName)} - ${group.sharedPermission === "EDIT" ? "Edit" : "View Only"}`,
                                groupType: "SHARED",
                                sharedMeta: group
                            }))
                        }
                    ];

                    setGroupOptions(groupOptions);
                }
            })
            .catch((error) => {
                toast.error("There was a problem loading some group information. Please try again later.");
                console.error(error);
            });
    }, [gradingProfiles, selectedEpr, selectedGrade, selectedTemplate, timePeriod, token, setGroupOptions]);

    const fetchAllSharedGroups = useCallback(() => {
        const effectiveTeacherGuid = overrideTeacherGuid || userDetails?.uid;

        if (!effectiveTeacherGuid || !token || !schoolYearKey || !timePeriod) return;

        const options = {
            action: "getAllSharedGroups",
            params: {
                teacherGuid: effectiveTeacherGuid,
                schoolYearKey: schoolYearKey,
                timePeriod: timePeriod
            },
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const sharedGroups = response.data.payload;
                    const manipulatedGroups = sharedGroups.map((group) => ({
                        value: `shared-${group.sharedGroupId}`,
                        label: `${group.sharedGroupCreatorDisplayName} - ${groupNameNormalizer(group.sharedGroupName)}`,
                        sharedGradingProfile: group.sharedGradingProfile,
                        groupType: "SHARED",
                        sharedGradeName: group.sharedGradeName,
                        sharedLocationKey: group.locationKey,
                        sharedMeta: group
                    }));

                    setAllSharedGroupOptions(manipulatedGroups);
                }
            })
            .catch((error) => {
                toast.error("There was a problem loading shared groups information. Please try again later.");
                console.error(error);
            });
    }, [schoolYearKey, setAllSharedGroupOptions, timePeriod, token, userDetails, overrideTeacherGuid]);
    return {
        fetchAllGroupOptions,
        fetchSelectedGroupInfo,
        fetchSelectedGroupStudentComments,
        fetchAllSharedGroups
    };
};

export default useGroupData;
